import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { StateMessage } from "@/components/StateMessage";
import { ScaleTicketImportDialog } from "@/components/ScaleTicketImportDialog";
import { GrainLoadDialog, GRAIN_UNIT_SHORT } from "@/components/GrainLoadDialog";
import type { PlantingOption } from "@/components/GrainLoadDialog";
import { demoApi, problemMessage } from "@/api/client";
import { grainBinLabel } from "@/lib/grainBinLabel";
import type { GrainBin, GrainLoad, Guid } from "@/api/types";
import "./GrainLoadsPanel.css";

export interface GrainLoadsPanelProps {
  /** The entity the Grain screen is showing (null while it is still resolving). */
  entityId: Guid | null;
  /** The screen's ACTIVE bins - farm and elevator alike (`locationKind`, `vendorName` say which). */
  bins: GrainBin[];
  /** Call after a load is recorded: `Grain.tsx` re-reads the whole screen so the bins' on-hand stays truthful. */
  onChanged: () => void;
}

/** One page of the newest-weighed-first list - the API's own default page size, stated so the card can say so. */
const PAGE_SIZE = 50;

const weighedFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const weightFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Status = "loading" | "ready" | "error";

/**
 * ef-063-1b-4 - the "Loads" card on `/farm/grain`: the scale tickets recorded so far (newest weighed first) and the
 * "Record a load" form. A saved load books its harvest into the chosen bin, so `onChanged` makes the screen re-read
 * the bins. Destination here is a BIN only; the straight-to-contract path and the CSV / JSON import are API only this
 * wave. Loads are immutable: there is no edit or delete.
 */
export function GrainLoadsPanel({ entityId, bins, onChanged }: GrainLoadsPanelProps) {
  const [status, setStatus] = useState<Status>("loading");
  const [errorText, setErrorText] = useState("");
  const [loads, setLoads] = useState<GrainLoad[]>([]);
  const [plantings, setPlantings] = useState<PlantingOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const importOpenerRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  useEffect(() => {
    if (entityId === null) return;
    const ctl = new AbortController();
    const year = new Date().getFullYear();
    Promise.all([
      demoApi.listGrainLoads({ entityId, limit: PAGE_SIZE }, ctl.signal),
      demoApi.getFieldCrops({ year }, ctl.signal),
      demoApi.getFields(entityId, ctl.signal),
    ])
      .then(([loadRows, fieldCrops, fields]) => {
        if (ctl.signal.aborted) return;
        const fieldName = new Map(fields.map((f) => [f.id, f.name]));
        const options = fieldCrops
          .filter((fc) => fc.entityId === entityId && fieldName.has(fc.fieldId))
          .map((fc) => ({ id: fc.id, label: `${fieldName.get(fc.fieldId)} — ${fc.crop}` }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setLoads(loadRows);
        setPlantings(options);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (ctl.signal.aborted) return;
        setErrorText(problemMessage(err, "Could not load the scale tickets."));
        setStatus("error");
      });
    return () => ctl.abort();
  }, [entityId, reloadTick]);

  const retry = useCallback(() => {
    setStatus("loading");
    setReloadTick((n) => n + 1);
  }, []);

  const binName = useMemo(() => new Map(bins.map((b) => [b.id, grainBinLabel(b)])), [bins]);
  const plantingLabel = useMemo(() => new Map(plantings.map((p) => [p.id, p.label])), [plantings]);

  const canRecord = status === "ready" && bins.length > 0;
  const dataState = status === "ready" && loads.length === 0 ? "empty" : status;

  function closeDialog(): void {
    setDialogOpen(false);
    openerRef.current?.focus();
  }

  function closeImport(): void {
    setImportOpen(false);
    importOpenerRef.current?.focus();
  }

  function handleImported(): void {
    onChanged();
    setReloadTick((n) => n + 1);
  }

  function handleSaved(): void {
    onChanged();
    setReloadTick((n) => n + 1);
    closeDialog();
  }

  return (
    <section className="grain-loads" aria-labelledby={headingId} data-state={dataState}>
      <div className="grain-loads__head">
        <h2 id={headingId} className="grain-loads__title">
          Loads
        </h2>
        <div className="grain-loads__actions">
        <button
          ref={importOpenerRef}
          type="button"
          className="btn btn--ghost"
          onClick={() => setImportOpen(true)}
          disabled={entityId === null || bins.length === 0}
        >
          Import tickets
        </button>
        <button
          ref={openerRef}
          type="button"
          className="btn btn--primary"
          onClick={() => setDialogOpen(true)}
          disabled={!canRecord}
        >
          Record a load
        </button>
        </div>
      </div>

      <p className="grain-loads__why">
        A load is one scale ticket. Saving it books the grain into the bin you choose, so the bin's on-hand moves.
        Recorded loads cannot be edited.
      </p>

      {status === "loading" && <StateMessage tone="loading" title="Loading loads…" />}

      {status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load the scale tickets."
          detail={errorText}
          action={
            <button type="button" className="btn btn--ghost" onClick={retry}>
              Try again
            </button>
          }
        />
      )}

      {status === "ready" && bins.length === 0 && (
        <StateMessage tone="empty" title="Add a bin first." detail="A load goes into a bin." />
      )}

      {status === "ready" && loads.length === 0 && bins.length > 0 && (
        <StateMessage tone="empty" title="No loads yet." detail="Record a scale ticket to book a load into a bin." />
      )}

      {status === "ready" && loads.length > 0 && (
        <>
          <ul className="grain-loads__list" aria-label="Loads">
            {loads.map((l) => {
              const planting = l.fieldCropId ? plantingLabel.get(l.fieldCropId) : undefined;
              const destination = l.binId ? (binName.get(l.binId) ?? "Bin") : "Contract";
              return (
                <li key={l.id} className="grain-loads__row" data-load-id={l.id}>
                  <div className="grain-loads__meta">
                    <span className="grain-loads__ticket">{l.ticketNo}</span>
                    <span className="grain-loads__sub">{planting ?? l.crop}</span>
                    <span className="grain-loads__sub">{weighedFormatter.format(new Date(l.weighedAt))}</span>
                  </div>
                  <div className="grain-loads__result">
                    <span className="grain-loads__net">
                      {weightFormatter.format(l.net)} {GRAIN_UNIT_SHORT[l.unit]} {l.crop}
                    </span>
                    <span className="grain-loads__sub">Into {destination}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          {loads.length >= PAGE_SIZE && (
            <p className="grain-loads__sub">Showing the {PAGE_SIZE} most recently weighed tickets.</p>
          )}
        </>
      )}

      {dialogOpen && <GrainLoadDialog bins={bins} plantings={plantings} onClose={closeDialog} onSaved={handleSaved} />}
      {importOpen && entityId !== null && (
        <ScaleTicketImportDialog entityId={entityId} bins={bins} onClose={closeImport} onImported={handleImported} />
      )}
    </section>
  );
}
